import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listThreads } from '../api/messaging.ts';

/**
 * How many `general` threads are sitting unclaimed right now — the mechanism `docs/decisions/
 * patient-portal-messaging-scope.md`'s "Notification" section names as the actual substitute for a
 * staff-side email/notification-preferences model in v1: "the live unread/unassigned count on the
 * inbox nav entry is the actual mechanism that keeps a new general-inbox message from going unseen."
 *
 * **This is an unassigned count, not an unread count — a deliberate, narrower scope than the
 * decision doc's own "unread/unassigned" phrasing, not an oversight.** `GET /api/inbox/threads`
 * (`server/src/messaging/routes.ts`) returns no per-thread read state at all: `message_thread_reads`
 * is written by `markThreadRead` on every detail view but never read back to any client, staff or
 * patient, by any existing route. A genuine per-thread "has this staff member seen the latest
 * message" badge (which would also cover a `care_team` thread, which has no `assignedToUserId` to
 * count as "unassigned" in the first place) needs that field added to the wire response first — a
 * backend change out of scope for this frontend-only pass, and flagged as a finding rather than
 * built around with a client-side guess. This hook counts only what the existing response already
 * proves: `assignedToUserId IS NULL AND status = 'open'`.
 *
 * Polled every 60s — the closest thing to "live" this app has anywhere without a websocket or push
 * channel, neither of which exists in this codebase for anything yet.
 */
export function useUnassignedInboxCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: ['inbox', 'unassigned-count'],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      listThreads({ kind: 'general', status: 'open', assigned: 'unassigned' }, signal),
    select: (threads) => threads.length,
    refetchInterval: 60_000,
  });
}
